import { uiText } from "@/lib/i18n/uiText";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/brand/Logo";
import { Loader2 } from "lucide-react";

type Details = {
  client?: { name?: string; client_name?: string; redirect_uri?: string } | null;
  redirect_uri?: string;
  scope?: string;
  redirect_url?: string;
  redirect_to?: string;
};
type OAuthApi = {
  getAuthorizationDetails: (id: string) => Promise<{ data: Details | null; error: { message: string } | null }>;
  approveAuthorization: (id: string) => Promise<{ data: Details | null; error: { message: string } | null }>;
  denyAuthorization: (id: string) => Promise<{ data: Details | null; error: { message: string } | null }>;
};
const oauth = () => (supabase.auth as unknown as { oauth: OAuthApi }).oauth;

export const Route = createFileRoute("/.lovable/oauth/consent")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Connect an AI assistant | Statlign" },
      { name: "description", content: "Allow an AI assistant to use your Statlign account and credits." },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Connect an AI assistant | Statlign" },
      { property: "og:description", content: "Allow an AI assistant to use your Statlign account and credits." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: Consent,
  errorComponent: ({ error }) => (
    <Shell><p className="text-destructive">{uiText("Something went wrong:")} {error instanceof Error ? error.message : uiText("please try again.")}</p></Shell>
  ),
});

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md p-6 space-y-5">
        <Logo />
        {children}
      </Card>
    </main>
  );
}

function Consent() {
  const id = new URLSearchParams(window.location.search).get("authorization_id");
  const [details, setDetails] = useState<Details | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<"approve" | "deny" | null>(null);

  useEffect(() => {
    (async () => {
      if (!id) return setError("This connection link is missing or invalid. Start the connection again from your assistant.");
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) {
        const here = window.location.pathname + window.location.search;
        window.location.replace(`/auth?redirect=${encodeURIComponent(here)}`);
        return;
      }
      setEmail(u.user.email ?? null);
      const { data, error } = await oauth().getAuthorizationDetails(id);
      if (error) return setError("This connection request has expired. Start again from your assistant.");
      const redirect = data?.redirect_url ?? data?.redirect_to;
      if (redirect && !data?.client) return window.location.assign(redirect);
      setDetails(data);
    })();
  }, [id]);

  const decide = async (approve: boolean) => {
    if (!id) return;
    setBusy(approve ? "approve" : "deny");
    const { data, error } = approve ? await oauth().approveAuthorization(id) : await oauth().denyAuthorization(id);
    const redirect = data?.redirect_url ?? data?.redirect_to;
    if (error || !redirect) {
      setBusy(null);
      return setError(approve ? "Couldn't approve the connection. Please try again." : "Couldn't cancel the connection. Please try again.");
    }
    window.location.assign(redirect);
  };

  if (error) return <Shell><p className="text-sm text-destructive">{error}</p></Shell>;
  if (!details) return <Shell><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></Shell>;

  const client = details.client?.name ?? details.client?.client_name ?? "An AI assistant";
  const redirectUri = details.client?.redirect_uri ?? details.redirect_uri;
  return (
    <Shell>
      <div className="space-y-2">
        <h1 className="text-xl font-semibold text-foreground">{uiText("Connect")} {client}  {uiText("to Statlign")}</h1>
        <p className="text-sm text-muted-foreground">
          {client}  {uiText("will be able to use Statlign as")} <strong className="text-foreground">{email}</strong>.
        </p>
      </div>
      <ul className="text-sm text-muted-foreground space-y-1.5 list-disc pl-5">
        <li>{uiText("Browse upcoming matches, odds and track record")}</li>
        <li>{uiText("Reveal picks using your credits (2 per pick, 1 for strategy insights)")}</li>
        <li>{uiText("See your credit balance")}</li>
        <li>{uiText("Share your basic profile and email address")}</li>
      </ul>
      {redirectUri && <p className="text-xs text-muted-foreground break-all">{uiText("Returns to:")} {redirectUri}</p>}
      <p className="text-xs text-muted-foreground">{uiText("This doesn't bypass your account's permissions. You can revoke access by changing your password.")}</p>
      <div className="flex gap-3">
        <Button className="flex-1" disabled={!!busy} onClick={() => decide(true)}>
          {busy === "approve" ? <Loader2 className="h-4 w-4 animate-spin" /> : uiText("Allow access")}
        </Button>
        <Button variant="outline" className="flex-1" disabled={!!busy} onClick={() => decide(false)}>
          {busy === "deny" ? <Loader2 className="h-4 w-4 animate-spin" /> : uiText("Cancel connection")}
        </Button>
      </div>
    </Shell>
  );
}
